import type { ConsensusTrendPoint } from '@app-types/performance';

export type ConsensusTrendKey = keyof Omit<ConsensusTrendPoint, 'label'>;
export type AdjustedBarValue = number | { value: number; rawValue: number } | null;

const CONSENSUS_TREND_KEYS: ConsensusTrendKey[] = [
  'bear',
  'slightBear',
  'neutral',
  'slightBull',
  'bull',
];

const CONSENSUS_AXIS_EMPTY_MAX = 10;
const CONSENSUS_AXIS_SMALL_RANGE_MAX = 25;
const CONSENSUS_AXIS_SMALL_INTERVAL = 5;
const CONSENSUS_AXIS_DEFAULT_INTERVAL = 10;
const CONSENSUS_AXIS_TARGET_INTERVALS = 5;

export const getRawConsensusTrendValue = (value: AdjustedBarValue | undefined) =>
  typeof value === 'number' ? value : value?.rawValue ?? null;

const getRenderedValue = (value: AdjustedBarValue | undefined): number =>
  typeof value === 'number' ? value : value?.value ?? 0;

// Same idea as widget-library BrokerRatingChart: keep tiny non-zero blocks
// visible enough for their labels while preserving the displayed raw number.
export const adjustConsensusTrendValue = (value: number | null): AdjustedBarValue => {
  if (value === null || !Number.isFinite(value)) {
    return null;
  }
  if (value > 0 && value <= 3) {
    return { value: 3 + value * 0.25, rawValue: value };
  }
  return value;
};

const toNiceInterval = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) {
    return CONSENSUS_AXIS_DEFAULT_INTERVAL;
  }

  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;

  return Math.max(CONSENSUS_AXIS_DEFAULT_INTERVAL, niceNormalized * magnitude);
};

export const computeConsensusTrendAxis = (
  data: ConsensusTrendPoint[],
): { max: number; interval: number } => {
  const maxStackTotal = Math.max(
    0,
    ...data.map((point) =>
      CONSENSUS_TREND_KEYS.reduce((total, key) => (
        total + getRenderedValue(adjustConsensusTrendValue(point[key]))
      ), 0),
    ),
  );

  if (maxStackTotal <= 0) {
    return { max: CONSENSUS_AXIS_EMPTY_MAX, interval: CONSENSUS_AXIS_SMALL_INTERVAL };
  }

  const interval = maxStackTotal <= CONSENSUS_AXIS_SMALL_RANGE_MAX
    ? CONSENSUS_AXIS_SMALL_INTERVAL
    : maxStackTotal <= 100
      ? CONSENSUS_AXIS_DEFAULT_INTERVAL
      : toNiceInterval(maxStackTotal / CONSENSUS_AXIS_TARGET_INTERVALS);
  const max = Math.max(interval, interval * Math.ceil(maxStackTotal / interval));

  return { max, interval };
};
